import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Save, Check, Target, Diamond, Upload, X } from 'lucide-react';
import { BADGE_ICONS, ICON_DISPLAY } from './constants';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Constants ── */
const STAMP_ICONS = {
  'Badges': ['badge-star', 'badge-medal', 'badge-check', 'badge-stamp'],
  'Populaires': ['star', 'heart', 'check', 'thumbs-up', 'sparkle', 'magic', 'trophy', 'target'],
  'Cafe / Resto': ['coffee', 'croissant', 'pizza', 'taco', 'sushi', 'pancakes', 'burger', 'cupcake'],
  'Commerce': ['bag', 'dress', 'lipstick', 'shoe', 'diamond', 'flower', 'gift', 'key'],
  'Boucherie': ['steak', 'meat', 'bacon', 'cow'],
  'Generique': ['circle', 'star-alt', 'heart-alt', 'diamond-alt'],
};


const getStampSize = (total) => {
  if (total <= 6) return { px: 44, cls: 'w-11 h-11', text: 'text-xl', rewardText: 'text-[10px]' };
  if (total <= 10) return { px: 40, cls: 'w-10 h-10', text: 'text-lg', rewardText: 'text-[9px]' };
  return { px: 34, cls: 'w-[34px] h-[34px]', text: 'text-base', rewardText: 'text-[8px]' };
};

/* ── Loyalty type selector card ── */
const TypeCard = ({ type, title, subtitle, description, example, sectors, icon: Icon, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`relative p-5 rounded-[14px] border-2 text-left transition-all duration-200 w-full
      ${selected
        ? type === 'stamps'
          ? 'border-[#2563EB] bg-blue-50/70 shadow-md'
          : 'border-purple-500 bg-purple-50/70 shadow-md'
        : 'border-[#E5E7EB] bg-white hover:border-slate-300 hover:shadow-sm'
      }`}
    data-testid={`card-type-${type}`}
  >
    {selected && (
      <div className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center
        ${type === 'stamps' ? 'bg-[#2563EB]' : 'bg-purple-500'}`}>
        <Check className="w-3.5 h-3.5 text-white" />
      </div>
    )}
    <div className="space-y-3">
      <div className="flex items-center gap-2.5">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center
          ${selected
            ? type === 'stamps' ? 'bg-blue-100' : 'bg-purple-100'
            : 'bg-slate-100'}`}>
          <Icon className={`w-5 h-5
            ${selected
              ? type === 'stamps' ? 'text-[#2563EB]' : 'text-purple-600'
              : 'text-slate-500'}`} />
        </div>
        <div>
          <p className="font-semibold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed">{description}</p>

      <div className={`text-xs font-medium px-3 py-1.5 rounded-full inline-block
        ${selected
          ? type === 'stamps' ? 'bg-blue-100 text-[#2563EB]' : 'bg-purple-100 text-purple-700'
          : 'bg-slate-100 text-slate-600'}`}>
        {example}
      </div>

      <div className="flex flex-wrap gap-1.5 pt-0.5">
        {sectors.map(s => (
          <span key={s} className="text-[11px] text-slate-400 bg-[#F9FAFB] border border-slate-100 px-2 py-0.5 rounded-md">{s}</span>
        ))}
      </div>
    </div>
  </button>
);

/* ══════════════════════════════════════
   MAIN EXPORT — RewardsTab
   ══════════════════════════════════════ */
export const RewardsTab = ({ formData, setFormData, handleChange, handleSubmit, saving, cardId, isNew, uploading, setUploading, setError }) => {
  const [showConfirmation, setShowConfirmation] = useState(!!formData.card_type);
  const [iconValidation, setIconValidation] = useState(null); // { ok, msg }
  const selectedIcon = formData.stamp_icon || '';
  const isCustomIcon = selectedIcon.startsWith('http') || selectedIcon.startsWith('/api/');
  const fileInputRef = useRef(null);
  const primaryColor = formData.primary_color || '#2563EB';
  const totalStamps = formData.total_stamps || 10;
  const stampSize = getStampSize(totalStamps);

  const selectType = (type) => {
    setFormData({ ...formData, card_type: type });
    setShowConfirmation(true);
  };

  return (
    <div className="grid lg:grid-cols-[1fr,480px] gap-8">
      <div className="space-y-6">

        {/* ── SECTION 1: Loyalty Type Selector ── */}
        <Card className="border-[#E5E7EB]">
          <CardHeader>
            <CardTitle>Type de fidélité</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <TypeCard
                type="stamps"
                title="Tampons"
                subtitle="Collecter et obtenir la récompense"
                description="Chaque visite = 1 tampon. Idéal pour fidéliser sur la fréquence."
                example="Ex : 10 visites → 1 café offert"
                sectors={['Salon de coiffure', 'Restaurant', 'Boulangerie', 'Coffee shop']}
                icon={Target}
                selected={formData.card_type === 'stamps'}
                onSelect={() => selectType('stamps')}
              />
              <TypeCard
                type="points"
                title="Points"
                subtitle="X points par achat"
                description="Chaque euro dépensé = X points. Idéal pour fidéliser sur le panier moyen."
                example="Ex : 500 points → 5€ de réduction"
                sectors={['Boutique mode', 'Cosmétiques', 'Épicerie fine', 'High-tech']}
                icon={Diamond}
                selected={formData.card_type === 'points'}
                onSelect={() => selectType('points')}
              />
            </div>

            {/* Confirmation banner */}
            {showConfirmation && formData.card_type && (
              <div className="flex items-center gap-2 p-3 rounded-lg text-sm font-medium bg-green-50 border border-green-200 text-green-700"
                data-testid="type-confirmation-banner">
                <Check className="w-4 h-4 flex-shrink-0" />
                Bon choix pour une fidélité basée sur {formData.card_type === 'stamps' ? 'la fréquence' : 'le panier'}.
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── SECTION 2: Type-specific configuration ── */}
        <Card className="border-[#E5E7EB]">
          <CardHeader>
            <CardTitle>
              {formData.card_type === 'stamps' ? 'Configuration des tampons' : 'Configuration des points'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">

              {formData.card_type === 'stamps' ? (
                <>
                  {/* ── Stamp count: number + range slider ── */}
                  <div className="space-y-3">
                    <Label htmlFor="total_stamps">Nombre de tampons pour obtenir la récompense</Label>
                    <p className="text-sm text-slate-500">Entre 2 et 20 tampons. La moyenne pour fidéliser sans décourager : 8–10.</p>
                    <div className="flex items-center gap-4">
                      <Input
                        id="total_stamps"
                        name="total_stamps"
                        type="number"
                        min="2"
                        max="20"
                        value={formData.total_stamps}
                        onChange={handleChange}
                        className="w-24 text-center text-lg font-semibold"
                        data-testid="total-stamps-input"
                      />
                      <div className="flex-1 space-y-1">
                        <input
                          type="range"
                          name="total_stamps"
                          min="2"
                          max="20"
                          value={formData.total_stamps || 10}
                          onChange={handleChange}
                          className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-200 accent-[#2563EB]"
                          data-testid="total-stamps-slider"
                        />
                        <div className="flex justify-between text-[11px] text-slate-400 px-0.5">
                          <span>2 (court terme)</span>
                          <span>20 (long terme)</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Stamp icon selector ── */}
                  <div className="space-y-3" data-testid="stamp-icon-selector">
                    <Label>Style des tampons</Label>
                    {Object.entries(STAMP_ICONS).map(([category, icons]) => (
                      <div key={category}>
                        <p className="text-xs text-slate-400 font-medium mb-1.5">{category}</p>
                        <div className="grid grid-cols-8 gap-1.5">
                          {icons.map(iconKey => {
                            const badgeInfo = BADGE_ICONS[iconKey];
                            return (
                              <button key={iconKey} type="button"
                                onClick={() => setFormData({ ...formData, stamp_icon: iconKey })}
                                className={`aspect-square rounded-lg flex items-center justify-center transition-all
                                  ${selectedIcon === iconKey
                                    ? 'ring-2 ring-offset-1 bg-white shadow-sm'
                                    : 'bg-slate-50 hover:bg-slate-100'}`}
                                style={selectedIcon === iconKey ? { border: `2px solid ${primaryColor}` } : {}}
                                data-testid={`stamp-icon-${iconKey}`}
                                title={badgeInfo ? badgeInfo.label : iconKey}
                              >
                                {badgeInfo
                                  ? <badgeInfo.Component size={22} color={primaryColor || badgeInfo.defaultColor} />
                                  : <span className="text-xl">{ICON_DISPLAY[iconKey] || iconKey}</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {selectedIcon && (
                      <button type="button" onClick={() => setFormData({ ...formData, stamp_icon: '' })}
                        className="text-xs text-slate-400 hover:text-red-500">
                        Retirer l'icone
                      </button>
                    )}

                    {/* ── Custom icon upload ── */}
                    <div className="space-y-3 pt-2" data-testid="custom-icon-upload">
                      {/* Guide */}
                      <div className="rounded-lg bg-slate-50 p-3 space-y-1">
                        <p className="text-xs text-slate-500 leading-relaxed">Votre icône doit être carrée</p>
                        <p className="text-xs text-slate-500 leading-relaxed">Taille minimum : 200 x 200 pixels</p>
                        <p className="text-xs text-slate-500 leading-relaxed">Fond transparent recommandé (PNG)</p>
                        <p className="text-xs text-slate-400 leading-relaxed italic">Conseil : utilisez votre logo simplifié</p>
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        data-testid="custom-icon-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setIconValidation(null);
                          if (isNew) { setIconValidation({ ok: false, msg: 'Enregistrez la carte d\'abord' }); return; }
                          if (!file.type.match(/^image\/(png|jpeg)$/)) {
                            setIconValidation({ ok: false, msg: `Utilisez PNG ou JPG (reçu : ${file.type})` });
                            return;
                          }
                          const img = new window.Image();
                          img.src = URL.createObjectURL(file);
                          img.onload = async () => {
                            const w = img.width, h = img.height;
                            URL.revokeObjectURL(img.src);
                            if (Math.abs(w - h) > Math.max(w, h) * 0.1) {
                              setIconValidation({ ok: false, msg: `Image non carrée (${w}px x ${h}px). Recadrez-la.` });
                              return;
                            }
                            if (w < 200 || h < 200) {
                              setIconValidation({ ok: false, msg: `Trop petite (${w} x ${h}). Minimum 200 x 200.` });
                              return;
                            }
                            setIconValidation({ ok: true, msg: 'Image valide' });
                            setUploading?.(true);
                            try {
                              const fd = new FormData();
                              fd.append('file', file);
                              const { data } = await axios.post(`${API_URL}/api/upload?kind=icon`, fd, { withCredentials: true });
                              setFormData(prev => ({ ...prev, stamp_icon: data.url }));
                            } catch (err) {
                              setIconValidation({ ok: false, msg: 'Échec de l\'import' });
                            } finally {
                              setUploading?.(false);
                            }
                          };
                        }}
                      />

                      {/* Validation message */}
                      {iconValidation && (
                        <p className={`text-xs font-medium ${iconValidation.ok ? 'text-green-600' : 'text-red-500'}`}
                          data-testid="icon-validation-msg">
                          {iconValidation.ok ? '\u2705 ' : '\u274c '}{iconValidation.msg}
                        </p>
                      )}

                      {/* Preview or upload button */}
                      {isCustomIcon ? (
                        <div className="flex items-center gap-4">
                          {/* Wallet-style preview */}
                          <div className="w-[100px] h-[100px] rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: primaryColor }}
                            data-testid="custom-icon-preview">
                            <div className="w-[80px] h-[80px] rounded-full bg-white flex items-center justify-center overflow-hidden">
                              <img src={selectedIcon.startsWith('/') ? `${API_URL}${selectedIcon}` : selectedIcon}
                                alt="Custom icon" className="w-[60px] h-[60px] object-contain" />
                            </div>
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <button type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="text-xs text-slate-600 hover:text-slate-800 underline"
                              disabled={uploading}>
                              Remplacer
                            </button>
                            <button type="button"
                              onClick={() => { setFormData({ ...formData, stamp_icon: '' }); setIconValidation(null); }}
                              className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1">
                              <X className="w-3 h-3" /> Supprimer
                            </button>
                          </div>
                        </div>
                      ) : (
                        <Button type="button" variant="outline" size="sm"
                          className="text-xs gap-1.5"
                          disabled={isNew || uploading}
                          onClick={() => { setIconValidation(null); fileInputRef.current?.click(); }}
                          data-testid="custom-icon-upload-btn">
                          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          Uploader mon icône
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* ── Reward progress text (custom) ── */}
                  <div className="space-y-2">
                    <Label htmlFor="reward_progress_text">Texte avant récompense</Label>
                    <Input id="reward_progress_text" name="reward_progress_text"
                      value={formData.reward_progress_text || ''} onChange={handleChange}
                      placeholder="Plus que {X} tampons avant la récompense"
                      maxLength={200}
                      data-testid="reward-progress-text-input" />
                    <p className="text-sm text-slate-500">
                      Tapez <code className="px-1 py-0.5 rounded bg-slate-100 text-[11px] font-mono">{'{X}'}</code> (avec les accolades) à l'endroit où vous voulez afficher le nombre restant. Exemple : <span className="italic">"plus que {'{X}'} crêpes"</span> deviendra <span className="italic">"plus que 7 crêpes"</span> sur le pass de votre client.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* ── Points: conversion rate ── */}
                  <div className="space-y-2">
                    <Label htmlFor="points_per_euro">Taux de conversion</Label>
                    <p className="text-sm text-slate-500">Combien de points le client gagne par euro dépensé.</p>
                    <div className="flex items-center gap-3">
                      <Input id="points_per_euro" name="points_per_euro" type="number" min="1"
                        value={formData.points_per_euro ?? formData.points_per_visit ?? 1} onChange={handleChange}
                        className="w-24 text-center text-lg font-semibold"
                        data-testid="points-per-euro-input" />
                      <span className="text-sm text-slate-500">point(s) par euro</span>
                    </div>
                  </div>

                  {/* ── Points: reward threshold ── */}
                  <div className="space-y-2">
                    <Label htmlFor="reward_threshold">Seuil de récompense</Label>
                    <p className="text-sm text-slate-500">Nombre de points nécessaires pour obtenir la récompense.</p>
                    <div className="flex items-center gap-3">
                      <Input id="reward_threshold" name="reward_threshold" type="number" min="1"
                        value={formData.reward_threshold} onChange={handleChange}
                        className="w-24 text-center text-lg font-semibold"
                        data-testid="reward-threshold-input" />
                      <span className="text-sm text-slate-500">points</span>
                    </div>
                  </div>

                  {/* ── Reward progress text (custom) ── */}
                  <div className="space-y-2">
                    <Label htmlFor="reward_progress_text">Texte avant récompense</Label>
                    <Input id="reward_progress_text" name="reward_progress_text"
                      value={formData.reward_progress_text || ''} onChange={handleChange}
                      placeholder="Plus que {X} points avant la récompense"
                      maxLength={200}
                      data-testid="reward-progress-text-input" />
                    <p className="text-sm text-slate-500">
                      Tapez <code className="px-1 py-0.5 rounded bg-slate-100 text-[11px] font-mono">{'{X}'}</code> (avec les accolades) à l'endroit où vous voulez afficher le nombre restant. Exemple : <span className="italic">"plus que {'{X}'} points"</span> deviendra <span className="italic">"plus que 50 points"</span> sur le pass de votre client.
                    </p>
                  </div>
                </>
              )}

              {/* ── Reward description (both types) ── */}
              <div className="space-y-2">
                <Label htmlFor="reward_description">Description de la récompense</Label>
                <Input id="reward_description" name="reward_description" value={formData.reward_description}
                  onChange={handleChange} placeholder="ex: 1 café offert" data-testid="reward-description-input" />
              </div>

              {/* ── Summary ── */}
              <div className="p-4 bg-blue-50 rounded-[14px]">
                <p className="text-sm text-blue-900">
                  {formData.card_type === 'stamps' ? (
                    <><strong>Résumé :</strong> Les clients collectent {totalStamps} tampons (1 par visite) pour obtenir : <strong>{formData.reward_description || '---'}</strong>. La carte se réinitialise après.</>
                  ) : (
                    <><strong>Résumé :</strong> Avec {formData.points_per_euro ?? formData.points_per_visit ?? 1} point(s) par euro et un seuil de {formData.reward_threshold}, les clients ont besoin de {Math.ceil(formData.reward_threshold / (formData.points_per_euro ?? formData.points_per_visit ?? 1))} visites pour obtenir : <strong>{formData.reward_description || '---'}</strong></>
                  )}
                </p>
              </div>

              <Button type="submit" className="w-full bg-[#2563EB] hover:bg-blue-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* ── Right panel: Live Preview (sticky) ── */}
      <div className="lg:sticky lg:top-5 self-start">
        <Card className="border-[#E5E7EB]">
          <CardHeader><CardTitle>Aperçu Apple Wallet</CardTitle></CardHeader>
          <CardContent>
            <div className="transform scale-[1.05] origin-top">
              <WalletPreview formData={formData} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════
   LIVE PREVIEW — Apple Wallet style
   ══════════════════════════════════════ */
const WalletPreview = ({ formData }) => {
  const primary = formData.primary_color || '#2563EB';
  const secondary = formData.secondary_color || '#FFFFFF';
  const total = formData.total_stamps || 10;
  const icon = formData.stamp_icon || '';
  const stampSize = getStampSize(total);
  const cols = total <= 5 ? total : 5;

  /* Points card variant */
  if (formData.card_type !== 'stamps') {
    return (
      <div className="max-w-[380px] mx-auto">
        <div className="rounded-2xl overflow-hidden shadow-xl" style={{ backgroundColor: primary }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ color: secondary }}>
            <span className="font-semibold text-sm truncate">{formData.name || 'Ma carte'}</span>
            <div className="text-right">
              <p className="text-[10px] opacity-70">POINTS</p>
              <p className="text-lg font-bold">0 pts</p>
            </div>
          </div>
          {/* Warm beige gradient placeholder */}
          <div className="h-24" style={{ background: `linear-gradient(135deg, ${primary}ee 0%, #c9a87c 50%, ${primary}cc 100%)` }} />
          <div className="px-4 py-3" style={{ color: secondary }}>
            <div className="flex justify-between mb-1">
              <div>
                <p className="text-[10px] uppercase opacity-60">MEMBRE</p>
                <p className="text-sm font-medium">John Doe</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase opacity-60">RÉCOMPENSE</p>
                <p className="text-sm">à {formData.reward_threshold || 10} pts</p>
              </div>
            </div>
          </div>
        </div>
        <PreviewWalletBadges />
      </div>
    );
  }

  /* Stamps card variant — overlay on banner */
  return (
    <div className="max-w-[380px] mx-auto">
      <div className="rounded-2xl overflow-hidden shadow-xl" style={{ backgroundColor: primary }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ color: secondary }}>
          <span className="font-semibold text-sm truncate">{formData.name || 'Ma carte'}</span>
          <div className="text-right">
            <p className="text-[10px] opacity-70">TAMPONS</p>
            <p className="text-lg font-bold">0/{total}</p>
          </div>
        </div>

        {/* Strip zone — banner with stamp overlay */}
        <div className="relative" style={{ aspectRatio: '2.6/1' }} data-testid="wallet-stamp-grid">
          <div className="w-full h-full"
            style={{ background: `linear-gradient(180deg, ${primary} 0%, ${primary}88 100%)` }} />
          {/* Stamp circles overlay at bottom */}
          <div className="absolute bottom-2.5 left-0 right-0 flex flex-col items-center gap-1 pointer-events-none">
            {(() => {
              const gridCols = Math.min(5, total);
              const gridRows = Math.ceil(total / gridCols);
              const sz = stampSize.px;
              return Array.from({ length: gridRows }, (_, row) => {
                const rowStart = row * gridCols;
                const rowCount = Math.min(gridCols, total - rowStart);
                return (
                  <div key={row} className="flex justify-center gap-1.5">
                    {Array.from({ length: rowCount }, (_, col) => {
                      const idx = rowStart + col;
                      return (
                        <div key={idx}
                          className={`${stampSize.cls} rounded-full flex items-center justify-center font-bold shrink-0`}
                          style={{ backgroundColor: 'rgba(255,255,255,0.5)', border: '2px solid rgba(255,255,255,0.7)' }}
                          data-testid={`preview-stamp-${idx}`}
                        />
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        <div className="px-4 py-3" style={{ color: secondary }}>
          <p className="text-[10px] opacity-60">RÉCOMPENSE</p>
          <p className="text-sm">{formData.reward_description || 'Cadeau offert'}</p>
        </div>
      </div>
      <PreviewWalletBadges />
    </div>
  );
};

/* Toggle badges at the bottom of the preview */
const PreviewWalletBadges = () => (
  <div className="flex items-center justify-center gap-3 mt-3">
    <span className="text-[11px] text-slate-500 px-3 py-1.5 rounded-full bg-slate-100 border border-[#E5E7EB] font-medium">
      Apple Wallet
    </span>
    <span className="text-[11px] text-slate-500 px-3 py-1.5 rounded-full bg-slate-100 border border-[#E5E7EB] font-medium">
      Google Wallet
    </span>
  </div>
);
